import styled, { css } from 'styled-components';
import { getColorWithAlpha } from '@elfsight/lib-utils';
import { MediaImage } from '@elfsight/widget-media';
import { createReadableClass } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import type { AppointmentBookingService } from 'widgets/appointment-booking/src/types';
import { DividerBlock } from '../../components/divider-block';
import { useWidgetDimensions } from '../../widget-dimensions-provider';
import { useServices } from '../services-provider';
import { ServiceCardAction } from './service-card-action';
import { ServiceCardContent } from './service-card-content';

type CurrentServiceCardProps = {
  service?: AppointmentBookingService;
  canChange?: boolean;
};

export function ServiceCard({ service, canChange }: CurrentServiceCardProps) {
  const { currentService } = useServices();
  const { textColor } = useSettings();
  const { isMobile, isSmallMobile } = useWidgetDimensions();

  if (!service) {
    return null;
  }

  const { picture, name } = service;

  return (
    <Container
      _isMobile={isMobile}
      _isSmallMobile={isSmallMobile}
      _isServiceSelected={!!currentService}
    >
      {picture && (
        <Image
          src={picture.url}
          alt={picture.alt || name}
          withSkeleton
          skeletonColor={getColorWithAlpha(textColor, 0.05)}
          mode="cover"
          containerWidth={isMobile ? '60px' : '70px'}
          containerHeight={isMobile ? '60px' : '70px'}
          _isMobile={isMobile}
        />
      )}
      <ServiceCardContent service={service} />

      {canChange && <ServiceCardAction service={service} />}
    </Container>
  );
}

const Container = styled(DividerBlock).attrs({
  className: createReadableClass({
    component: 'service-card',
    element: 'container'
  })
})<{
  _isSmallMobile: boolean;
  _isMobile: boolean;
  _isServiceSelected: boolean;
}>`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;
  padding: 20px 0;
  width: 100%;
  gap: ${({ _isSmallMobile, _isMobile, _isServiceSelected }) => {
    if (_isSmallMobile) {
      return _isServiceSelected ? '4px 16px' : '16px';
    }

    if (_isMobile) {
      return '4px 16px';
    }

    return '4px 20px';
  }};
`;

const Image = styled(MediaImage).attrs({
  className: createReadableClass({
    component: 'service-card',
    element: 'image'
  })
})<{
  _isMobile: boolean;
}>`
  border-radius: 14px;
  flex-shrink: 0;
  ${({ _isMobile }) =>
    _isMobile &&
    css`
      align-self: flex-start;
    `}
`;
